import { z } from "zod";

/**
 * Energy overpayment check — "are you overpaying for electricity vs the
 * regulated reference price?"
 *
 * Two factual-comparison surfaces:
 *  - `overpaymentVsReference(zone, input)` — compare the user's current plan
 *    (annual $ directly, OR usage kWh + supply/usage rates we annualise) against
 *    the AER **Default Market Offer (DMO)** residential reference price for their
 *    distribution zone, returning how far above/below the regulated reference
 *    they sit, as a cited factual comparison (never a "switch and save $X" claim).
 *  - `loyaltyTaxEstimate(input)` — surface the ACCC-published "loyalty tax": the
 *    gap between what new-plan customers pay and what longstanding customers on
 *    old plans pay, as a CITED RANGE (not a personalised saving).
 *
 * VERSIONING — DATE-KEYED to the DMO determination year, NOT a code constant.
 * The DMO reprices annually effective **1 July** (AER final determination,
 * released late May). A new determination = a NEW dated ruleset file
 * (`dmo8-2026-27.ts`), registered alongside the prior one and NEVER mutated.
 * Historical "X% over" verdicts must resolve against the determination in force
 * on the assessed date. The assessment date is always passed in (no `Date.now()`).
 *
 * SCOPE / HONESTY (panel ship-conditions, encoded):
 *  - This compares to the REGULATED REFERENCE PRICE (DMO), not a live market
 *    scrape. Retailers' market offers move continuously; we do not claim to know
 *    the cheapest live plan — the government's free Energy Made Easy (NSW/QLD/SA)
 *    is the authoritative next step, surfaced in copy.
 *  - VICTORIA is OUT OF SCOPE: VIC uses the ESC's Victorian Default Offer (VDO),
 *    a different mechanism. A VIC zone returns an explicit out-of-scope flag, not
 *    a wrong DMO comparison.
 *  - REGULATORY: electricity retail is NOT a s764A financial product (AFSL-clean)
 *    and the energy-switching affiliate is a permitted handoff — but it is kept
 *    OFF this rules surface entirely. There is no `handoff`/`partner`/affiliate
 *    field by construction; this module is facts only. A guard
 *    (`assertNoAffiliateOnResult`) makes that executable + tested.
 */

/**
 * AER DMO distribution zones shipped here (electricity, residential, single-rate
 * / no controlled load). These are the three DMO jurisdictions (NSW / SE QLD /
 * SA) the AER sets a reference price for. VIC is handled separately (VDO) and is
 * flagged out of scope.
 */
export const DMO_ZONES = [
  "AUSGRID", // NSW — Sydney, Central Coast, Hunter
  "ENDEAVOUR", // NSW — Sydney's greater west, Blue Mountains, Illawarra
  "ESSENTIAL", // NSW — regional / country NSW
  "ENERGEX", // SE QLD — Brisbane + South East Queensland
  "SA_POWER_NETWORKS", // South Australia
] as const;
export type DmoZone = (typeof DMO_ZONES)[number];

/**
 * Any zone the user might select — DMO zones plus VICTORIA, which is in the UI
 * but out of this module's scope (it's VDO, not DMO). Passing VICTORIA returns an
 * out-of-scope result rather than a wrong comparison.
 */
export const ENERGY_ZONES = [...DMO_ZONES, "VICTORIA"] as const;
export type EnergyZone = (typeof ENERGY_ZONES)[number];

/** Per-zone DMO residential reference price (single-rate, no controlled load). */
export interface DmoZoneReference {
  /** Annual reference price for a representative residential customer, whole $. */
  annualReferencePrice: number;
  /** Human label for the zone, e.g. "Ausgrid (Sydney, Central Coast, Hunter)". */
  label: string;
}

/** Provenance + audit metadata for a dated DMO determination period. */
export interface DmoRuleSetMeta {
  /** AER determination number, e.g. "DMO 8". */
  determination: string;
  /** Human label of the period, e.g. "2026-27". */
  period: string;
  /** Inclusive ISO start date the determination is effective from (1 July). */
  effectiveFrom: string;
  /** Inclusive ISO end date the determination governs (30 June). */
  effectiveTo: string;
  /** Source-of-truth URLs the figures were transcribed from. */
  sources: Record<string, string>;
  /** Date the source pages were last verified, ISO. */
  sourcesVerifiedAt: string;
}

/** A complete DMO ruleset for one determination period (date-keyed). */
export interface EnergyOverpaymentRuleSet extends DmoRuleSetMeta {
  /** Per-zone DMO reference prices. */
  zones: Record<DmoZone, DmoZoneReference>;
  /**
   * ACCC-published "loyalty tax" figures: how much more longstanding customers on
   * old plans pay than new-plan customers, on average. Encoded as a RANGE so copy
   * surfaces it as a cited range, never a personalised saving. Each end carries
   * its own ACCC report provenance (the two figures come from different report
   * rounds — keep the dates explicit).
   */
  loyaltyTax: {
    /** Lower-bound average annual gap (offers >1 year old), $/yr. */
    lowerAnnual: number;
    /** Upper-bound average annual gap (longer/older plans), $/yr. */
    upperAnnual: number;
    /** Plain-English description of the lower-bound figure's basis + report. */
    lowerBasis: string;
    /** Plain-English description of the upper-bound figure's basis + report. */
    upperBasis: string;
  };
}

// --- Overpayment-vs-reference surface -----------------------------------------

/**
 * The user's current electricity plan, supplied EITHER as a single annual dollar
 * figure OR as the bill components we annualise (daily supply charge + usage rate
 * × annual usage). Exactly one path must be supplied.
 */
export const PlanInputSchema = z
  .object({
    /** Distribution zone (or VICTORIA, which is out of scope). */
    zone: z.enum(ENERGY_ZONES),
    /**
     * The assessed date (ISO, YYYY-MM-DD). Passed in, never derived from the
     * clock, so a historical comparison resolves against the DMO determination in
     * force then.
     */
    assessmentDate: z.string(),
    /** Current plan's annual cost in dollars, if the user knows it directly. */
    currentAnnualCost: z.number().min(0).optional(),
    /**
     * Bill-component path (used when `currentAnnualCost` is absent):
     * daily supply charge ($/day) + usage rate (c/kWh as DOLLARS/kWh) × annual
     * usage (kWh). We annualise supply over 365 days.
     */
    usage: z
      .object({
        /** Daily supply (service-to-property) charge, $/day. */
        dailySupplyCharge: z.number().min(0),
        /** Usage (consumption) rate in DOLLARS per kWh (e.g. 0.30 = 30c/kWh). */
        usageRatePerKwh: z.number().min(0),
        /** Annual electricity usage in kWh. */
        annualUsageKwh: z.number().min(0),
      })
      .optional(),
  })
  .refine(
    (v) => v.currentAnnualCost !== undefined || v.usage !== undefined,
    "supply either currentAnnualCost or the usage components",
  );
/** Caller-facing input (pre-parse). */
export type PlanInput = z.input<typeof PlanInputSchema>;

/** Which side of the regulated reference the user's plan sits on. */
export type ReferenceBand = "above_reference" | "at_reference" | "below_reference";

export interface OverpaymentOutput {
  /** The DMO zone the comparison ran against. */
  zone: DmoZone;
  /** The plan's annualised cost the comparison used, whole dollars. */
  currentAnnualCost: number;
  /** The DMO residential reference price for the zone, whole dollars. */
  referencePrice: number;
  /**
   * currentAnnualCost − referencePrice, whole dollars. Positive = above the
   * reference (paying more than the benchmark); negative = below. A FACTUAL
   * difference between two figures, never framed as a "saving".
   */
  differenceFromReference: number;
  /**
   * The difference as a fraction of the reference price (e.g. 0.10 = 10% above).
   * Positive above, negative below.
   */
  percentFromReference: number;
  /** Which side of the reference the plan sits on. */
  band: ReferenceBand;
  /**
   * True when the plan is more than 10% above the reference — the ACCC-flagged
   * "materially over the default offer" cohort. Descriptive flag, not advice.
   */
  materiallyAbove: boolean;
}

// --- Loyalty-tax surface ------------------------------------------------------

export const LoyaltyTaxInputSchema = z.object({
  /** The assessed date (ISO) — resolves the DMO/ACCC period. */
  assessmentDate: z.string(),
});
export type LoyaltyTaxInput = z.input<typeof LoyaltyTaxInputSchema>;

export interface LoyaltyTaxOutput {
  /** Lower bound of the cited average annual loyalty-tax gap, $/yr. */
  lowerAnnual: number;
  /** Upper bound of the cited average annual loyalty-tax gap, $/yr. */
  upperAnnual: number;
  /** Basis line for the lower bound (which ACCC report + plan age). */
  lowerBasis: string;
  /** Basis line for the upper bound (which ACCC report + plan age). */
  upperBasis: string;
}

// --- Out-of-scope (Victoria / VDO) result ------------------------------------

export interface OutOfScopeOutput {
  /** Discriminant — the zone is not covered by this DMO module. */
  outOfScope: true;
  /** The zone that was out of scope. */
  zone: "VICTORIA";
  /** Machine-readable reason. */
  reason: "victoria_uses_vdo";
}

/**
 * Result of `overpaymentVsReference`: either a DMO comparison, or an explicit
 * out-of-scope marker for Victoria (VDO, not DMO). Callers discriminate on the
 * `outOfScope` field.
 */
export type OverpaymentResult = OverpaymentOutput | OutOfScopeOutput;

/** Type guard: is this an out-of-scope (Victoria) result? */
export function isOutOfScope(r: OverpaymentResult): r is OutOfScopeOutput {
  return "outOfScope" in r && r.outOfScope === true;
}
